import type { QueryClient } from '@w/react-query'
import type { SpaceResource } from '../router/spaces-resources'

type Rename = { from: string; to: string }
type State = { followed: Rename | null; closed: readonly string[] }

/**
 * Database renames of one workspace file (spaces-resources.ts opens one per file, beside its queryClient):
 * - followed: the latest rename this device made or re-applied. A server refusal can undo it (rollback) and Re-apply
 *   can redo it after the route moved, so the open database follows to the surviving name (database-page.tsx).
 * - closed: tables whose open grid hides its reads while a rename made outside the rename dialog runs (closeDatabaseReads).
 * Same shape as lexicon/adopt-bump-errors.ts: owned state read through a query (product code may not import
 * @w/react-store, .oxlintrc.json INV-1). The state lives here, not in the query data, so any refetch or broad
 * invalidation reads the same value back; writes publish it at once with setQueryData.
 */
export function createDatabaseRenames(queryClient: QueryClient) {
  let state: State = { followed: null, closed: [] }
  const queryKey = ['database-renames']
  function set(next: State) {
    state = next
    queryClient.setQueryData(queryKey, state)
  }
  return {
    options: () => ({ queryKey, queryFn: () => Promise.resolve(state), initialData: state, staleTime: Infinity, refetchOnWindowFocus: false }),
    follow(followed: Rename) { set({ ...state, followed }) },
    /** Drops `followed` once the route left both of its names. */
    expire(followed: Rename) { if (state.followed?.from === followed.from && state.followed.to === followed.to) set({ ...state, followed: null }) },
    close(tables: readonly string[]) { if (tables.length > 0) set({ ...state, closed: [...state.closed, ...tables] }) },
    reopen(tables: readonly string[]) { if (state.closed.some(table => tables.includes(table))) set({ ...state, closed: state.closed.filter(table => !tables.includes(table)) }) },
  }
}

export type DatabaseRenames = ReturnType<typeof createDatabaseRenames>

/** The grid's reads of one table (database-grid.tsx query keys). */
export function removeTableReads(resource: Pick<SpaceResource, 'queryClient'> & { space: { id: string } }, table: string): void {
  resource.queryClient.removeQueries({ queryKey: ['database-properties', resource.space.id, table] })
  resource.queryClient.removeQueries({ queryKey: ['database-rows', resource.space.id, table] })
}

/** Generated by database-sql.ts renameDatabase: `ALTER TABLE "<from>" RENAME TO "<to>"`. */
const renameSql = /^ALTER TABLE "((?:[^"]|"")+)" RENAME TO "((?:[^"]|"")+)"$/u

/** A rename made outside the rename dialog (a dead letter's Re-apply, dead-letter-queue/panel.tsx) reads its names from
 * the letter's SQL and closes the open grid's reads of the old name first, as the dialog does, so no read of a table
 * the DDL removes stays live and no "no such table" error shows. Statements that rename no table close nothing. The
 * returned settle reopens the reads of a rename that did not apply; an applied one becomes `followed`, so the open
 * database follows the new name with or without a dialog rename in this page (database-page.tsx), and stays closed
 * until that route change unmounts the grid (database-grid.tsx). */
export function closeDatabaseReads(resource: SpaceResource, statements: readonly string[]): (applied: boolean) => void {
  const renames = statements.flatMap(sql => {
    const renamed = renameSql.exec(sql)
    return renamed ? [{ from: renamed[1].replaceAll('""', '"'), to: renamed[2].replaceAll('""', '"') }] : []
  })
  const tables = renames.map(rename => rename.from)
  resource.databaseRenames.close(tables)
  for (const table of tables) removeTableReads(resource, table)
  return applied => {
    const followed = renames.at(-1)
    if (applied && followed) resource.databaseRenames.follow(followed)
    else resource.databaseRenames.reopen(tables)
  }
}

/** Re-apply of one dead letter, the same from View failures (dead-letter-queue/panel.tsx) and Settings → Sync problems
 * (sync/sync-problems.tsx): a rename closes the open grid's reads first and is followed once it applied
 * (closeDatabaseReads). True when the letter left the list (it applied); a failed re-apply keeps it with its new reason. */
export async function reapplyDeadLetter(resource: SpaceResource, letter: { id: string; statements: readonly string[] }): Promise<boolean> {
  const settle = closeDatabaseReads(resource, letter.statements)
  try {
    await resource.deadLetters.reapply(letter.id)
    const applied = !(await resource.deadLetters.list()).some(problem => problem.id === letter.id)
    settle(applied)
    return applied
  } catch (error) { settle(false); throw error }
}
